import type { ServerType } from './ServerType'
import type { ConfigLayerOptions } from '../ConfigLayer'

import PoziAPIData from 'api/data'
import type { StyleGeoStylerOptions } from 'config/catalog/StyleGeoStyler'
import PoziNotifications from 'utils/notifications'

import { ConfigLayerCapabilityWFS } from './ConfigLayerCapabilityWFS'
import { ConfigLayerCapabilityWMS } from './ConfigLayerCapabilityWMS'
import { ConfigLayerCapabilityWMTS } from './ConfigLayerCapabilityWMTS'
import { ConfigLayerQGISProjectSettings } from './ConfigLayerQGISProjectSettings'
import KeywordsParser from './KeywordsParser'
import { ConfigLayerType } from '../ConfigLayerType'

export interface ConfigLayerCapabilityOptions extends ConfigLayerOptions {
  type: ConfigLayerType.QGIS_PROJECT_SETTINGS |
    ConfigLayerType.WFS_GET_CAPABILITIES |
    ConfigLayerType.WMS_GET_CAPABILITIES |
    ConfigLayerType.WMTS_CAPABILITIES
  serverType?: ServerType
  title: string
  titleid: string
  group: string
  groupid: string
  config?: {
    spatial: {
      url: string
      proxyHeaders?: Record<string, string>
      proxyUrl?: string

      // Config-defined Params that we want passed in layer requests e.g., "BBOX": "-38.27,144.66,-38.24,144.71"
      params?: Record<string, unknown>

      projection?: string

      // For WMS Catalogues
      tiled?: boolean
      tileSize?: number
  }
  },
  flatten?: boolean
  folder?: boolean
  legend?: string
  outputFormats?: Record<string, string>
  profiles?: string | string[]
  sortLayers?: boolean
  static?: boolean
  styleGeoStyler?: StyleGeoStylerOptions
}

export class ConfigLayerCapability {
  static async fetchUrl (url: string) {
    // FIXME: QGIS can take about 45 seconds to re-initialise after recycling
    try {
      return await PoziAPIData().ajax({
        url,
        dataType: 'text',
        timeout: 60000
      })
    } catch (error: any) { // TODO: Use correct type for error object
      console.error('Error fetching Catalog layer configuration', error.responseText)
      PoziNotifications().warning(`Error loading URL: ${url}`)
    }
  }

  static async getCapability (configLayerCapability: ConfigLayerCapabilityOptions) {
    try {
      switch (configLayerCapability.type) {
        case ConfigLayerType.QGIS_PROJECT_SETTINGS:
          return await ConfigLayerQGISProjectSettings.fetchCapability(configLayerCapability)
        case ConfigLayerType.WFS_GET_CAPABILITIES:
          return await ConfigLayerCapabilityWFS.fetchCapability(configLayerCapability)
        case ConfigLayerType.WMS_GET_CAPABILITIES:
          return await ConfigLayerCapabilityWMS.fetchCapability(configLayerCapability)
        case ConfigLayerType.WMTS_CAPABILITIES:
          return await ConfigLayerCapabilityWMTS.fetchCapability(configLayerCapability)
        default:
          console.error('Layer type is not handled: ', configLayerCapability.type)
      }
    } catch (error) {
      console.error('Error getting Catalog layer configuration', configLayerCapability.titleid, error)
      PoziNotifications().warning(`Error load ing catalog: ${configLayerCapability.title}`)
    }
  }

  static getConfigFromKeywords (keywords: string[]) {
    // console.debug('ConfigLayerCapability - getConfigFromKeywords', keywords)

    // Keyword options are special config settings set in the keywords of a layer
    // since that currently is the only way to configure overrides in QGIS
    const configOptions = KeywordsParser.getOptions(keywords)

    // console.debug('ConfigLayerCapability - getConfigFromKeywords - keyword options', titleid, configOptions)

    return configOptions
  }
}
